import React, { useState } from "react";
import { Layers, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

// Owner → Pattern Library: 3-piece back glass imported as separate Left/Center/Right (or twice via "Part N") → one set.
// Dry-run report first, then Apply. Copies go to library_trash (restorable), nothing is deleted without the preview.
export const ConsolidateSets = ({ onApplied }) => {
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  const run = async (dry) => {
    setBusy(dry ? "scan" : "apply");
    try {
      const r = await api.adminConsolidateSets(dry);
      setRes(r);
      if (!dry) {
        toast.success(`Done — ${r.applied?.sets || 0} sets linked, ${r.applied?.renamed || 0} renamed, ${r.applied?.trashed || 0} copies moved to trash`);
        onApplied?.();
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const openIt = () => { setOpen(true); setRes(null); run(true); };
  const applied = res && !res.dry_run;
  return (
    <>
      <button data-testid="lib-consolidate-btn" className="btn flex-none gap-1 px-2 text-[11px]" onClick={openIt} title="Group separately imported multi-piece templates — 3-piece back glass (Left / Center / Right, Part 1-3) and 2-piece windshield eyebrows (…1 / …2, Left / Right) — into one set. Preview first">
        <Layers className="h-3.5 w-3.5" /> Multi-piece sets
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden p-0" data-testid="consolidate-dialog">
          <div className="border-b border-white/10 px-5 py-4">
            <DialogTitle className="text-[15px] font-semibold text-[#e8e9ec]">Consolidate multi-piece sets</DialogTitle>
            <DialogDescription className="mt-1 text-[12px] text-[#9aa0a8]">
              Separate Left / Center / Right templates and "Part 1-3" copies of the same back glass, and windshield eyebrow halves imported one at a time ("… 1" / "… 2", Left / Right), become ONE row that adds every piece to the roll together.
              Near-identical copies are moved to the library trash (restorable). Nothing changes until you press Apply.
            </DialogDescription>
          </div>
          <div className="max-h-[55vh] overflow-y-auto px-5 py-3" data-testid="consolidate-report">
            {!res || busy === "scan" ? <div className="py-6 text-center text-[12px] text-[#9aa0a8]"><Loader2 className="inline h-4 w-4 animate-spin" /> Scanning the library…</div>
              : !res.report.length ? <div className="py-6 text-center text-[12px] text-[#9aa0a8]" data-testid="consolidate-empty">Nothing to consolidate — every multi-piece back glass and eyebrow is already one set.</div>
              : res.report.map((r) => <SetCard key={`${r.vehicle_id}|${r.set}`} r={r} />)}
          </div>
          {res && busy !== "scan" && !!res.report.length && (
            <div className="flex items-center justify-between gap-3 border-t border-white/10 px-5 py-3">
              <span className="font-mono text-[11px] text-[#9aa0a8]" data-testid="consolidate-summary">
                {res.vehicles} vehicle{res.vehicles === 1 ? "" : "s"} · {res.sets} set{res.sets === 1 ? "" : "s"} · {res.renamed} renamed · <span className={res.trashed ? "text-[#f0a35e]" : ""}>{res.trashed} to trash</span>
                {applied && <span className="ml-2 text-[#3fb950]">— applied</span>}
              </span>
              <div className="flex gap-2">
                <button className="btn px-3 text-[12px]" onClick={() => setOpen(false)} data-testid="consolidate-close">{applied ? "Close" : "Cancel"}</button>
                {!applied && <button className="btn-red px-3 text-[12px]" disabled={!!busy} onClick={() => run(false)} data-testid="consolidate-apply">{busy === "apply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Apply to {res.sets} set{res.sets === 1 ? "" : "s"}</button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const SetCard = ({ r }) => {
  const keep = r.actions.filter((a) => !a.trash_dup_of);
  const trash = r.actions.filter((a) => a.trash_dup_of);
  const sets = [...new Set(keep.map((a) => a.set_name))];
  return (
    <div className="glass-row mb-2 px-3 py-2 text-[12px]" data-testid={`consolidate-set-${r.vehicle_id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <b className="text-[#e8e9ec]">{r.vehicle}</b>
        <span className="font-mono text-[11px] text-[#4c9ffe]">{r.set}</span>
      </div>
      {sets.map((s) => (
        <div key={s} className="mt-1.5">
          <div className="text-[11px] uppercase tracking-wide text-[#9aa0a8]">→ one row: <span className="text-[#e8e9ec]">{s}</span></div>
          <ul className="ml-3 mt-0.5 space-y-0.5">
            {keep.filter((a) => a.set_name === s).map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline gap-x-2 text-[#c9ccd1]">
                <span className="w-12 text-[#9aa0a8]">{a.pos}</span>
                <span>{a.name}</span>
                {a.new_name !== a.name && <span className="text-[#9aa0a8]">renamed → <span className="text-[#e8e9ec]">{a.new_name}</span></span>}
                <span className="font-mono text-[10px] text-[#6b7079]">{a.size}"</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {!!trash.length && (
        <ul className="ml-3 mt-1.5 space-y-0.5">
          {trash.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline gap-x-2 text-[#f0a35e]">
              <span className="w-12">trash</span><span className="line-through">{a.name}</span>
              <span className="text-[#9aa0a8]">copy of {a.trash_dup_of}</span>
              <span className="font-mono text-[10px] text-[#6b7079]">{a.size}"</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
